// Offline connectivity + local-assessments state (Fase 1).
//
// Connectivity source of truth = actual backend reachability, NOT navigator.onLine
// (which reports "has a network interface", staying true on a LAN with no internet).
// - A successful API response (axios interceptor) marks us online.
// - A network error triggers a reachability probe; if it fails, we go offline.
// - While offline, we poll the probe so we can recover to online even when the
//   navigator never fires an 'online' event (LAN-with-no-internet case).
import { listSnapshots, getQueue, clearAssessment } from '@/plugins/idb'
import { buildSyncPayload } from '@/services/assessmentOffline'

const MAX_OFFLINE_ASSESSMENTS = 3
const PROBE_TIMEOUT_MS = 5000
const POLL_INTERVAL_MS = 15000
const FORCED_OFFLINE_KEY = 'forcedOffline'

let pollTimer = null
// One auto-sync at a time: the list can remount (or login + list) while a run
// is still uploading, and a second run would resend the same batches.
let autoSyncRunning = false

// User-chosen "lock offline" flag, persisted so it survives reloads and the SW.
// Kept OUT of `isOnline` (raw reachability) so the /offline home can still detect
// real connectivity and offer the way back.
function readForcedOffline() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(FORCED_OFFLINE_KEY) === 'true'
  } catch (e) {
    return false
  }
}

// Current user id for scoping local snapshots. On a hard reload refreshOfflineState
// runs at boot (main.js), BEFORE App.vue rehydrates the session store from storage,
// so the session getter is still empty. Fall back to the persisted session (same
// source the route middlewares read) so scoping isn't wrongly empty on reload —
// otherwise a downloaded assessment reopens in ONLINE mode after a refresh.
export function resolveCurrentUserId(rootGetters) {
  const fromStore = rootGetters['session/userData']()?.id
  if (fromStore != null) return fromStore
  try {
    const raw = (typeof localStorage !== 'undefined' && localStorage.getItem('session'))
      || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('session'))
    return raw ? (JSON.parse(raw)?.user?.id ?? null) : null
  } catch (e) {
    return null
  }
}

export default {
  namespaced: true,

  state: () => ({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    forcedOffline: readForcedOffline(),
    offlineAssessments: [],
    // Lines the back has checked out offline by the session user (`my-offline`),
    // tagged with that user's id: logout doesn't reload the app, so a list left
    // by user A must not count against user B on a shared device. null = unknown.
    myOfflineLines: null,
    // Lines whose admin-release auto-sync request is in flight: their manual
    // Sync buttons stay disabled meanwhile (a second send would duplicate it).
    autoSyncingLineIds: [],
    pendingSyncCount: 0,
    connectivityBound: false,
  }),

  mutations: {
    SET_ONLINE(state, value) {
      state.isOnline = value
    },
    SET_FORCED_OFFLINE(state, value) {
      state.forcedOffline = value
      try {
        if (typeof localStorage !== 'undefined') {
          if (value) localStorage.setItem(FORCED_OFFLINE_KEY, 'true')
          else localStorage.removeItem(FORCED_OFFLINE_KEY)
        }
      } catch (e) { /* storage unavailable — keep in-memory only */ }
    },
    SET_OFFLINE_ASSESSMENTS(state, ids) {
      state.offlineAssessments = ids
    },
    SET_MY_OFFLINE_LINES(state, value) {
      state.myOfflineLines = value
    },
    ADD_AUTO_SYNCING(state, id) {
      if (!state.autoSyncingLineIds.includes(id)) state.autoSyncingLineIds = [...state.autoSyncingLineIds, id]
    },
    REMOVE_AUTO_SYNCING(state, id) {
      state.autoSyncingLineIds = state.autoSyncingLineIds.filter(x => x !== id)
    },
    SET_PENDING_SYNC_COUNT(state, count) {
      state.pendingSyncCount = count
    },
    SET_CONNECTIVITY_BOUND(state) {
      state.connectivityBound = true
    },
  },

  actions: {
    // Bind listeners once, at app boot, and confirm reachability + start polling.
    initConnectivity({ commit, state, dispatch }) {
      if (!state.connectivityBound && typeof window !== 'undefined') {
        // A definite 'offline' event means offline; on 'online' we still probe,
        // because the interface can be up without real backend reachability.
        window.addEventListener('online', () => dispatch('checkReachability'))
        window.addEventListener('offline', () => commit('SET_ONLINE', false))
        commit('SET_CONNECTIVITY_BOUND')
      }
      dispatch('checkReachability')
      dispatch('startReachabilityPolling')
    },

    // Ping the backend with a no-cors HEAD: it resolves for ANY server response
    // (even 401/404 = reachable) and rejects only on a real network failure.
    // Same-origin assets can't be used — the SW serves them from cache offline.
    async checkReachability({ commit }) {
      if (typeof fetch === 'undefined') return
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
      try {
        await fetch(process.env.VUE_APP_API_URL, {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-store',
          signal: controller.signal,
        })
        commit('SET_ONLINE', true)
      } catch (e) {
        commit('SET_ONLINE', false)
      } finally {
        clearTimeout(timer)
      }
    },

    // Manual "lock offline" toggle: force the app into offline mode so a flaky
    // connection can't fire unintended requests. The router guard + main.js
    // watcher (both keyed on the effective `isOnline` getter) redirect to the
    // offline home. Cleared from the offline home's "back online" flow.
    lockOffline({ commit }) {
      commit('SET_FORCED_OFFLINE', true)
    },
    unlockOffline({ commit, dispatch }) {
      commit('SET_FORCED_OFFLINE', false)
      // Re-confirm real reachability so `isOnline` reflects the truth right away.
      dispatch('checkReachability')
    },

    startReachabilityPolling({ state, dispatch }) {
      if (pollTimer || typeof window === 'undefined') return
      pollTimer = setInterval(() => {
        // Only probe while offline — recovers us to online without a nav event.
        if (!state.isOnline) dispatch('checkReachability')
      }, POLL_INTERVAL_MS)
    },

    // Recompute local state from IndexedDB (downloaded ids + pending changes).
    // IndexedDB is per-ORIGIN, not per-user: on a shared device a snapshot
    // downloaded by user A stays in the DB after user B logs in. We must scope
    // to the current session user by the snapshot's `profile.id`, or B would see
    // A's assessment as their own offline copy — able to edit and sync it under
    // B's identity, and to read A's scores/comments/demographics. A missing
    // profile counts as not-mine (safer to hide than to leak).
    async refreshOfflineState({ commit, rootGetters }) {
      try {
        const currentUserId = resolveCurrentUserId(rootGetters)
        const snapshots = await listSnapshots()
        const mine = currentUserId == null
          ? []
          : snapshots.filter(s => s.profile && s.profile.id === currentUserId)
        const myIds = mine.map(s => s.id)
        commit('SET_OFFLINE_ASSESSMENTS', myIds)
        const queue = await getQueue()
        commit('SET_PENDING_SYNC_COUNT', queue.filter(c => myIds.includes(c.lineId)).length)
      } catch (e) {
        // IndexedDB unavailable (private mode, etc.) — degrade gracefully.
        console.error('refreshOfflineState failed', e)
        commit('SET_OFFLINE_ASSESSMENTS', [])
        commit('SET_PENDING_SYNC_COUNT', 0)
      }
    },

    // Ask the back which lines the session user has checked out offline, on ANY
    // device. The download limit is per user, not per browser, so it can't be
    // derived from IndexedDB alone. Errors propagate: callers decide.
    async fetchMyOfflineLines({ commit, rootGetters }, api) {
      const userId = resolveCurrentUserId(rootGetters)
      if (userId == null) return []
      const {
        evaluation_line_ids: ids = [],
        max_downloads: max,
        // Lines taken away from the user (admin release, QIP deleted) that may
        // still hold unsynced changes in some browser of theirs.
        pending_sync_line_ids: pendingSyncIds = [],
        // The subset released on purpose, by an admin or the release_offline_lines
        // command (not a deleted QIP): synced automatically (autoSyncReleasedLines).
        released_by_admin_line_ids: releasedByAdminIds = [],
      } = await api.getMyOfflineLines()
      commit('SET_MY_OFFLINE_LINES', {
        userId, ids, max, pendingSyncIds, releasedByAdminIds,
      })

      return ids
    },

    // Drop local copies whose offline lock no longer exists on the back (admin
    // released it, DB restored, synced from another device...). Without this a
    // stale snapshot keeps counting toward the download limit forever.
    // Snapshots with queued changes are kept: discarding unsynced work must be
    // the user's call (offline home "discard"), never a silent cleanup.
    // A dropped copy the back still lists as pending sync had nothing to send:
    // tell it (dismiss-pending-sync) so the reminder goes away. Only the browser
    // that had the copy may do that — another device can't know if one exists.
    // `api` is passed in because plugins/axios imports the store (circular).
    async reconcileWithServer({ dispatch, state, rootGetters }, api) {
      const currentUserId = resolveCurrentUserId(rootGetters)
      if (currentUserId == null) return
      try {
        // Snapshots are read BEFORE asking the back: a download in flight claims
        // the lock before saving its snapshot, so any snapshot listed here is
        // already reflected in the server answer and can't be wrongly dropped.
        const snapshots = await listSnapshots()
        const mine = snapshots.filter(s => s.profile && s.profile.id === currentUserId)
        // Fetched even with no local copies: it also feeds the download limit.
        const serverIds = await dispatch('fetchMyOfflineLines', api)
        if (!mine.length) return
        const orphans = mine.filter(s => !serverIds.includes(s.id))
        if (!orphans.length) return
        const queue = await getQueue()
        const toDrop = orphans.filter(s => !queue.some(c => c.lineId === s.id))
        const pendingSyncIds = (state.myOfflineLines && state.myOfflineLines.pendingSyncIds) || []
        await Promise.all(toDrop.map(async s => {
          if (pendingSyncIds.includes(s.id)) {
            // Best effort: if it fails the reminder stays, which is harmless.
            await api.dismissPendingSync(s.id).catch(e => console.error('dismissPendingSync failed', e))
          }
          await clearAssessment(s.id)
        }))
        if (toDrop.length) await dispatch('fetchMyOfflineLines', api).catch(() => {})
        if (toDrop.length) await dispatch('refreshOfflineState')
      } catch (e) {
        // Back unreachable or IndexedDB unavailable — keep local state as is.
        console.error('reconcileWithServer failed', e)
      }
    },

    // Super admin only: give up their own checkout WITHOUT applying the local
    // changes. Releasing themselves leaves no pending-sync record on the back
    // (no notification either), so the changes are archived right after via
    // offline-discarded — accepted now that the lock is gone — for a later
    // review/replay, and the local copy is dropped. Errors from the release
    // propagate (nothing changed); the archive step is best effort.
    async releaseOwnLineWithoutChanges({ dispatch }, { api, lineId }) {
      await api.releaseOfflineLine(lineId)
      try {
        const queue = await getQueue(lineId)
        if (queue.length) await api.discardOfflineLine(lineId, buildSyncPayload(lineId, queue))
      } catch (e) {
        console.error('releaseOwnLineWithoutChanges: archive failed', e)
      }
      await clearAssessment(lineId)
      await dispatch('refreshOfflineState')
      await dispatch('fetchMyOfflineLines', api).catch(() => {})
    },

    // Lines an admin released while this browser held unsynced changes for
    // them: send those changes without asking. The back archives them for
    // admin review (applied: false) and the local copy is dropped. Only the
    // admin-released ones: a deleted QIP's copy stays for a manual sync, and
    // copies still holding the lock are never touched. Run after
    // reconcileWithServer (it needs a fresh my-offline). Resolves to how many
    // lines were sent; a failed one keeps its copy and is retried next time.
    async autoSyncReleasedLines({ commit, dispatch, getters }, api) {
      if (autoSyncRunning) return 0
      autoSyncRunning = true
      let sent = 0
      try {
        const lineIds = getters.releasedByAdminLocalIds
        lineIds.forEach(lineId => commit('ADD_AUTO_SYNCING', lineId))
        await Promise.all(lineIds.map(async lineId => {
          try {
            await api.syncOffline(lineId, buildSyncPayload(lineId, await getQueue(lineId)))
            await clearAssessment(lineId)
            sent += 1
          } catch (e) {
            console.error('autoSyncReleasedLines failed for line', lineId, e)
          } finally {
            commit('REMOVE_AUTO_SYNCING', lineId)
          }
        }))
        if (sent) {
          await dispatch('refreshOfflineState')
          await dispatch('fetchMyOfflineLines', api).catch(() => {})
        }
      } finally {
        autoSyncRunning = false
      }

      return sent
    },
  },

  getters: {
    // Effective online: reachable AND not manually locked offline. This is what
    // the whole app keys off (router redirect, chips, EvaluationDetail.isOffline).
    isOnline: state => state.isOnline && !state.forcedOffline,
    // Raw backend reachability, ignoring the manual lock — used by the offline
    // home to know whether returning online is possible.
    isReachable: state => state.isOnline,
    isForcedOffline: state => state.forcedOffline,
    offlineAssessments: state => state.offlineAssessments,
    isDownloadedLocally: state => id => state.offlineAssessments.includes(id),
    isAutoSyncing: state => id => state.autoSyncingLineIds.includes(id),
    // Local copies of mine whose lock the back no longer gives me (released by
    // an admin, QIP deleted). reconcileWithServer only keeps those with queued
    // changes, so each one is unsynced work to send for admin review.
    lostLockLocalIds: (state, getters, rootState, rootGetters) => {
      const myLines = state.myOfflineLines
      if (!myLines || myLines.userId !== resolveCurrentUserId(rootGetters)) return []

      return state.offlineAssessments.filter(id => !myLines.ids.includes(id))
    },
    // Lost-lock copies released on purpose (admin or command): synced automatically.
    releasedByAdminLocalIds: (state, getters) => {
      const released = (state.myOfflineLines && state.myOfflineLines.releasedByAdminIds) || []

      return getters.lostLockLocalIds.filter(id => released.includes(id))
    },
    // The other lost-lock copies (QIP deleted, or a back that doesn't send the
    // admin-released list yet): the user syncs them by hand from the banner.
    manualReviewLocalIds: (state, getters) => getters.lostLockLocalIds
      .filter(id => !getters.releasedByAdminLocalIds.includes(id)),
    pendingSyncCount: state => state.pendingSyncCount,
    offlineCount: state => state.offlineAssessments.length,
    // Union of the back's checkouts (other devices) and this browser's copies (a
    // download just made, before the next `my-offline` fetch). Falls back to the
    // local copies alone while the back's list is unknown (offline, not fetched).
    // The back enforces the real limit; this only drives the button state.
    canDownloadMore: (state, getters, rootState, rootGetters) => {
      const myLines = state.myOfflineLines
      const known = myLines && myLines.userId === resolveCurrentUserId(rootGetters) ? myLines : null
      const serverIds = known ? known.ids : []
      const max = (known && known.max) || MAX_OFFLINE_ASSESSMENTS

      return new Set([...state.offlineAssessments, ...serverIds]).size < max
    },
  },
}
